import { Box, styled, useTheme } from '@mui/material';
import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { Typography } from '../../../components/Typography/Typography';
import { useAtomValue } from 'jotai';
import { approvalDetailsModalState, tsApprovalsSelectedOYearFilterState } from '../../../state/UIAccountantState';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccountantTsStatusLabel } from '../../../utils/tsMappers';
import { SelectItem, TimesheetStatus, UserRole } from '../../../types';
import { calculateDaysBetweenDates, getMonthLabelShort } from '../../../utils/formatters';
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar';
import { format, parseISO } from 'date-fns';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import { TableV1 } from '../../../components/Table/TableV1';
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useApprovalDetailsModalColumns } from '../hooks/useApprovalDetailsModalColumns';
import { useApprovalDetailsTableData } from '../hooks/useApprovalDetailsTableData';
import { useMonthlyTimesheetsToExport } from '../../../queries/hooks/useMonthlyTimesheetsToExport';
import { generateActivityLogsTemplate } from '../../../utils/excelMultiExportTimesheet';
import { useFilteredOpsTimesheets } from '../../../queries/hooks/useFilteredOpsTimesheets';

interface ApprovalDetailsModalProps {
  approvalDetailsOpen: boolean;
  toggleApprovalDetails: () => void;
}

export const ApprovalDetailsModal = ({
  approvalDetailsOpen,
  toggleApprovalDetails,
}: ApprovalDetailsModalProps) => {
  const { colors } = useTheme();
  const approvalDetailsState = useAtomValue(approvalDetailsModalState);
  const selectedYear = useAtomValue(tsApprovalsSelectedOYearFilterState);
  const [selectedCycle, setSelectedCycle] = useState<SelectItem | null>(null);
  const [shouldExport, setShouldExport] = useState(false);

  const { data: operatingPartners } = useFilteredOpsTimesheets(`${selectedYear}-01`, `${selectedYear}-12`);

  const timesheetData = useMemo(() => {
    if (!operatingPartners || !approvalDetailsState) return null;

    const { userId, tsId } = approvalDetailsState;
    const op = operatingPartners.find((op) => op.id === userId);
    if (!op) return null;

    const timesheet = op.monthlyTimesheets?.find((ts) => ts.id === tsId);
    if (!timesheet) return null;

    const sortedLogs = [...(timesheet.timesheetLogs || [])].sort(
      (a, b) => new Date(a.actionStart).getTime() - new Date(b.actionStart).getTime()
    );

    return {
      op,
      timesheet: { ...timesheet, timesheetLogs: sortedLogs },
      statusLabel: getAccountantTsStatusLabel(timesheet.status as TimesheetStatus),
      submittedOn: sortedLogs[0]?.actionEnd,
      daysOpen: calculateDaysBetweenDates(sortedLogs[0].actionStart),
      dealsCount: timesheet.timesheetRows.filter((ts) => ts.dealId !== 0).length,
      cycle: Math.max(...sortedLogs.map((log) => log.cycle)),
      allCycles: [...new Set(sortedLogs.map((log) => log.cycle))],
    };
  }, [operatingPartners, approvalDetailsState]);

  useEffect(() => {
    if (!timesheetData) return;
    setSelectedCycle({
      id: timesheetData?.cycle,
      value: `Cycle ${timesheetData?.cycle.toString()}`,
    });
  }, [timesheetData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const tableData = useApprovalDetailsTableData(
    timesheetData?.timesheet.timesheetLogs ?? [],
    Number(selectedCycle?.id)
  );
  const columns = useApprovalDetailsModalColumns();

  const table = useReactTable({
    data: tableData ?? [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableRowSelection: true,
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const handleSelectCycle = useCallback((value: SelectItem) => {
    setSelectedCycle(value);
  }, []);

  const { data: timesheets, isLoading: isLoadingTSExport } = useMonthlyTimesheetsToExport({
    userIds: timesheetData?.op.id.toString() ?? '',
    timeRange: `${timesheetData?.timesheet.month}-${timesheetData?.timesheet.year}`,
    all: true,
    enabled: shouldExport,
    role: UserRole.ADMIN,
  });

  const handleExportDetails = useCallback(() => {
    setShouldExport(true);
  }, []);

  useEffect(() => {
    if (shouldExport && !isLoadingTSExport && timesheets) {
      setShouldExport(false);
      generateActivityLogsTemplate(
        timesheets,
        `Activity_Logs-${timesheetData?.op.name}-${getMonthLabelShort(
          timesheetData?.timesheet.month ?? 0
        )} ${timesheetData?.timesheet.year}`
      );
    }
  }, [
    shouldExport,
    isLoadingTSExport,
    timesheets,
    timesheetData?.op.name,
    timesheetData?.timesheet.month,
    timesheetData?.timesheet.year,
  ]);

  if (!timesheetData) return null;

  const { op, timesheet, statusLabel, submittedOn, daysOpen, dealsCount, cycle, allCycles } =
    timesheetData;

  return (
    <ModalComponent
      isOpen={approvalDetailsOpen}
      onClose={toggleApprovalDetails}
      actionButtonsProps={{
        onSubmit: handleExportDetails,
        onCancel: toggleApprovalDetails,
        submitText: 'Export',
        submitBtnStyle: {
          background: colors.iconStatus.active,
        },
        isSubmitDisabled: isLoadingTSExport,
      }}
      title='Approval Details'
      top='80px'
      width='943px'
    >
      <Wrapper>
        <Typography variant='h5' color={colors.text.main}>
          Displays the full TS process with detailed information for all roles.
        </Typography>
        <TimesheetDetails>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Timesheet Name
            </Typography>
            <Box display='flex' alignItems='center' gap='4px'>
              <UserAvatar name={op.name} size='medium' />
              <Typography variant='subtitle1' color={colors.text.main}>
                {`${op.name}-${getMonthLabelShort(timesheet.month)} ${timesheet.year}`}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Status
            </Typography>
            <Typography variant='subtitle1' color={colors.text.main}>
              {statusLabel}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Submitted on
            </Typography>
            <Typography variant='subtitle1' color={colors.text.main}>
              {submittedOn ? format(parseISO(submittedOn), 'MMM d, yyyy') : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Days Open
            </Typography>
            <Typography
              variant='subtitle1'
              color={colors.text.main}
              style={{
                textAlign: 'right',
              }}
            >
              {daysOpen}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Deals
            </Typography>
            <Typography
              variant='subtitle1'
              color={colors.text.main}
              style={{
                textAlign: 'right',
              }}
            >
              {dealsCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant='body' color={colors.text.caption} style={{ marginBottom: '8px' }}>
              Cycle
            </Typography>
            <Typography
              variant='subtitle1'
              color={colors.text.main}
              style={{
                textAlign: 'right',
              }}
            >
              {cycle}
            </Typography>
          </Box>
        </TimesheetDetails>
        <SingleSelect
          options={allCycles.map((cycle) => {
            return {
              id: cycle,
              value: `Cycle ${cycle.toString()}`,
            };
          })}
          onChange={(e, newValue) => {
            if (!newValue) return;
            handleSelectCycle(newValue);
          }}
          value={selectedCycle}
          sx={{
            backgroundColor: colors.surfaceBackground.bg4,
            borderRadius: '8px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '8px',
              borderColor: 'transparent',
            },
          }}
          hideClearIcon={true}
        />
        <TableV1
          table={table}
          height='auto'
          isLastColumnSticky={false}
          wrapperStyle={{
            maxHeight: '372px',
            overflowX: 'scroll',
            border: `1px solid ${colors.border.default}`,
            borderRadius: '4px',
          }}
        />
      </Wrapper>
    </ModalComponent>
  );
};

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TimesheetDetails = styled('div')`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg4};
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;
